import Swiper from "swiper";
import { Navigation, Autoplay, EffectFade, Pagination } from "swiper/modules";

interface ParallaxState {
  currentX: number;
  currentY: number;
  targetX: number;
  targetY: number;
}

export const initEmuHomeCarousel = (): void => {
  const carouselElement =
    document.querySelector<HTMLElement>(".emu-home-carousel");
  if (!carouselElement) return;

  // WeakMap to strictly track X/Y coordinates for parallax layers
  const parallaxStates = new WeakMap<HTMLElement, ParallaxState>();

  // 1. Explicitly select the navigation and pagination DOM nodes INSIDE this specific carousel
  const nextEl = carouselElement.querySelector<HTMLElement>(
    ".emu-home-carousel__next",
  );
  const prevEl = carouselElement.querySelector<HTMLElement>(
    ".emu-home-carousel__prev",
  );
  const paginationEl = carouselElement.querySelector<HTMLElement>(
    ".emu-home-carousel__pagination",
  );

  new Swiper(carouselElement, {
    modules: [Navigation, Autoplay, EffectFade, Pagination],
    loop: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    // 2. Pass the exact DOM nodes instead of CSS selector strings
    navigation: {
      nextEl: nextEl,
      prevEl: prevEl,
    },
    pagination: {
      el: paginationEl,
      clickable: true,
    },
    on: {
      init: function (swiperInstance: any) {
        let _animationFrameId: number | null = null;
        const easing = 0.08;

        const animateSprites = () => {
          const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
          if (!activeSlide) return;

          const parallaxSprites = activeSlide.querySelectorAll(
            ".mouse-parallax-sprite",
          ) as NodeListOf<HTMLElement>;

          parallaxSprites.forEach((sprite) => {
            if (!parallaxStates.has(sprite)) {
              parallaxStates.set(sprite, {
                currentX: 0,
                currentY: 0,
                targetX: 0,
                targetY: 0,
              });
            }

            const state = parallaxStates.get(sprite)!;
            state.currentX += (state.targetX - state.currentX) * easing;
            state.currentY += (state.targetY - state.currentY) * easing;
            sprite.style.transform = `translate3d(${state.currentX}px, ${state.currentY}px, 0)`;
          });

          _animationFrameId = requestAnimationFrame(animateSprites);
        };

        swiperInstance.el.addEventListener("mousemove", (e: MouseEvent) => {
          const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
          if (!activeSlide) return;

          const parallaxSprites = activeSlide.querySelectorAll(
            ".mouse-parallax-sprite",
          ) as NodeListOf<HTMLElement>;
          if (!parallaxSprites.length) return;

          const { clientX, clientY } = e;
          const { left, top, width, height } =
            swiperInstance.el.getBoundingClientRect();

          const x = (clientX - left - width / 2) / (width / 2);
          const y = (clientY - top - height / 2) / (height / 2);

          parallaxSprites.forEach((sprite) => {
            const speed = parseFloat(sprite.dataset.mouseParallaxSpeed || "0");
            const state = parallaxStates.get(sprite);

            if (state) {
              state.targetX = x * speed * 100;
              state.targetY = y * speed * 100;
            }
          });
        });

        swiperInstance.el.addEventListener("mouseleave", () => {
          const allSprites = swiperInstance.el.querySelectorAll(
            ".mouse-parallax-sprite",
          ) as NodeListOf<HTMLElement>;

          allSprites.forEach((sprite) => {
            const state = parallaxStates.get(sprite);
            if (state) {
              state.targetX = 0;
              state.targetY = 0;
            }
          });
        });

        animateSprites();
      },
    },
  });

  console.log(
    "[EmuHomeCarousel] Initialized successfully. Navigation isolated.",
  );
};
